import React, { useState, useEffect } from 'react';
import { X, Check, Minus, ShoppingBag, ArrowRight, Share2, Copy, Mail, Twitter, Facebook } from 'lucide-react';
import { Button } from './ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Share Dropdown for comparison
 */
const ShareDropdown = ({ productIds, onClose }) => {
  const shareUrl = `${window.location.origin}/products/compare?products=${productIds.join(',')}`;
  
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
      onClose();
    } catch (e) {
      toast.error('Failed to copy link');
    }
  };

  const shareEmail = () => {
    const subject = encodeURIComponent('Check out this product comparison from Apollo Shades');
    const body = encodeURIComponent(`I found some great window treatments I wanted to share with you:\n\n${shareUrl}`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
    onClose();
  };

  const shareTwitter = () => {
    const text = encodeURIComponent('Check out this product comparison from Apollo Shades');
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(shareUrl)}`, '_blank');
    onClose();
  };

  const shareFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
    onClose();
  };

  return (
    <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border py-2 z-20">
      <button 
        onClick={copyLink}
        className="w-full px-4 py-2 text-left text-sm hover:bg-neutral-100 flex items-center gap-3"
      >
        <Copy className="w-4 h-4" /> Copy Link
      </button>
      <button 
        onClick={shareEmail}
        className="w-full px-4 py-2 text-left text-sm hover:bg-neutral-100 flex items-center gap-3"
      >
        <Mail className="w-4 h-4" /> Email
      </button>
      <button 
        onClick={shareTwitter}
        className="w-full px-4 py-2 text-left text-sm hover:bg-neutral-100 flex items-center gap-3"
      >
        <Twitter className="w-4 h-4" /> Twitter
      </button>
      <button 
        onClick={shareFacebook}
        className="w-full px-4 py-2 text-left text-sm hover:bg-neutral-100 flex items-center gap-3"
      >
        <Facebook className="w-4 h-4" /> Facebook
      </button>
    </div>
  );
};

/**
 * Product Comparison Drawer
 * Shows side-by-side comparison of up to 3 products
 */
const ProductComparison = ({ 
  selectedProducts = [], 
  onRemove, 
  onClear,
  isOpen,
  onClose 
}) => {
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showShareMenu, setShowShareMenu] = useState(false);

  useEffect(() => {
    if (selectedProducts.length >= 2 && isOpen) {
      fetchComparison();
    }
  }, [selectedProducts, isOpen]);

  const fetchComparison = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API_URL}/api/products/compare`, 
        selectedProducts.map(p => p.product_type || p.id)
      );
      setComparisonData(response.data);
    } catch (err) {
      console.error('Failed to fetch comparison:', err);
      setError('Failed to load comparison data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="bg-white w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-t-2xl sm:rounded-2xl shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
            <div>
              <h2 className="text-xl font-semibold">Compare Products</h2>
              <p className="text-sm text-neutral-500">
                {selectedProducts.length} of 3 products selected
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Share button */}
              <div className="relative">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowShareMenu(!showShareMenu)}
                  disabled={selectedProducts.length < 2}
                >
                  <Share2 className="w-4 h-4 mr-1" /> Share
                </Button>
                {showShareMenu && (
                  <ShareDropdown 
                    productIds={selectedProducts.map(p => p.product_type || p.id)}
                    onClose={() => setShowShareMenu(false)}
                  />
                )}
              </div>
              <Button variant="outline" size="sm" onClick={onClear}>
                Clear All
              </Button>
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-auto max-h-[calc(90vh-80px)]">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : error ? (
              <div className="text-center py-20 px-6">
                <p className="text-red-500 mb-4">{error}</p>
                <Button onClick={fetchComparison}>Try Again</Button>
              </div>
            ) : selectedProducts.length < 2 ? (
              <div className="text-center py-20 px-6">
                <p className="text-neutral-500 mb-4">
                  Select at least 2 products to compare
                </p>
                <Button onClick={onClose}>Continue Shopping</Button>
              </div>
            ) : comparisonData ? (
              <div className="p-6">
                {/* Product Cards */}
                <div className={`grid gap-4 mb-8 ${
                  comparisonData.products.length === 2 ? 'grid-cols-2' : 'grid-cols-3'
                }`}>
                  {comparisonData.products.map((product, idx) => (
                    <div key={product.id} className="relative">
                      {/* Remove button */}
                      <button
                        onClick={() => onRemove(product.product_type || product.id)}
                        className="absolute -top-2 -right-2 z-10 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      
                      {/* Product card */}
                      <div className="border rounded-xl overflow-hidden">
                        {product.image && (
                          <div className="aspect-[4/3] bg-neutral-100">
                            <img 
                              src={product.image.startsWith('/') ? `${API_URL}${product.image}` : product.image}
                              alt={product.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <div className="p-4">
                          <p className="text-xs text-amber-600 uppercase tracking-wider mb-1">
                            {product.category}
                          </p>
                          <h3 className="font-semibold text-lg">{product.title}</h3>
                          <p className="text-sm text-neutral-500 mt-1 line-clamp-2">
                            {product.description}
                          </p>
                          <div className="mt-3 flex items-center justify-between">
                            <span className="text-lg font-semibold text-amber-600">
                              From ${product.price_from}
                            </span>
                            <Link to={product.cta_link}>
                              <Button size="sm" className="bg-amber-500 hover:bg-amber-600">
                                <ShoppingBag className="w-4 h-4 mr-1" /> Configure
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Specifications Table */}
                {comparisonData.specs.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold mb-4">Specifications</h3>
                    <div className="border rounded-xl overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-neutral-50">
                          <tr>
                            <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600 w-1/4">
                              Specification
                            </th>
                            {comparisonData.products.map((p) => (
                              <th key={p.id} className="text-center px-4 py-3 text-sm font-medium">
                                {p.title}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {comparisonData.specs.map((spec) => (
                            <tr key={spec} className="hover:bg-neutral-50">
                              <td className="px-4 py-3 text-sm text-neutral-600 capitalize">
                                {spec.replace(/_/g, ' ')}
                              </td>
                              {comparisonData.products.map((p) => (
                                <td key={p.id} className="px-4 py-3 text-center text-sm">
                                  {p.specifications[spec] || (
                                    <Minus className="w-4 h-4 mx-auto text-neutral-300" />
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Features Comparison */}
                {comparisonData.features.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold mb-4">Features</h3>
                    <div className="border rounded-xl overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-neutral-50">
                          <tr>
                            <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600 w-1/4">
                              Feature
                            </th>
                            {comparisonData.products.map((p) => (
                              <th key={p.id} className="text-center px-4 py-3 text-sm font-medium">
                                {p.title}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {comparisonData.features.filter(f => f).map((feature) => (
                            <tr key={feature} className="hover:bg-neutral-50">
                              <td className="px-4 py-3 text-sm text-neutral-600">
                                {feature}
                              </td>
                              {comparisonData.products.map((p) => (
                                <td key={p.id} className="px-4 py-3 text-center">
                                  {p.features[feature] ? (
                                    <Check className="w-5 h-5 mx-auto text-green-500" />
                                  ) : (
                                    <Minus className="w-4 h-4 mx-auto text-neutral-300" />
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Benefits */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Key Benefits</h3>
                  <div className={`grid gap-4 ${
                    comparisonData.products.length === 2 ? 'grid-cols-2' : 'grid-cols-3'
                  }`}>
                    {comparisonData.products.map((p) => (
                      <div key={p.id} className="border rounded-xl p-4">
                        <h4 className="font-medium mb-3">{p.title}</h4>
                        <ul className="space-y-2">
                          {(p.benefits || []).map((benefit, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm">
                              <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                              <span>{typeof benefit === 'string' ? benefit : (benefit.title || benefit.text || '')}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * Floating Compare Button that shows when products are selected
 */
export const CompareFloatingButton = ({ count, onClick }) => {
  if (count < 1) return null;

  return (
    <motion.button
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      onClick={onClick}
      className="fixed bottom-6 right-6 z-40 bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-2 font-medium"
    >
      Compare ({count})
      <ArrowRight className="w-4 h-4" />
    </motion.button>
  );
};

export default ProductComparison;
